import React, { Component } from "react";
import ReactTable from "react-table";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  Badge,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import { Colxx } from "../../../../components/common/CustomBootstrap";
import { Pagination } from "../../../../containers/pages/general";
import IntlMessages from "../../../../helpers/IntlMessages";
import { injectIntl } from "react-intl";
import axios from "axios";
import { END_POINT } from "../../../../constants/defaultValues";
import {
  NotificatioErrController,
  NotificatioSucController,
  AddCommaNumber,
} from "../../../../helpers/Utils";

const STATUS_META = {
  draft: { label: "Nháp", color: "secondary" },
  processing: { label: "Đang chờ ký", color: "warning" },
  pending: { label: "Đang chờ xử lý", color: "warning" },
  issued: { label: "Đã phát hành", color: "success" },
  success: { label: "Đã phát hành", color: "success" },
  signed: { label: "Đã ký", color: "success" },
  sent: { label: "Đã gửi khách hàng", color: "success" },
  cancelled: { label: "Đã hủy", color: "danger" },
  error: { label: "Phát hành lỗi", color: "danger" },
  failed: { label: "Phát hành lỗi", color: "danger" },
};

const ISSUANCE_STATUS_OPTIONS = [
  { value: "", labelId: "einvoice.filter.all" },
  { value: "pending", labelId: "einvoice.issuance-status.pending" },
  { value: "success", labelId: "einvoice.issuance-status.success" },
  { value: "failed", labelId: "einvoice.issuance-status.failed" },
];

const INVOICE_STATUS_OPTIONS = [
  { value: "", labelId: "einvoice.filter.all" },
  { value: "draft", labelId: "einvoice.invoice-status.draft" },
  { value: "issued", labelId: "einvoice.invoice-status.issued" },
];

const money = (value) => `${AddCommaNumber(Math.round(Number(value || 0)))} đ`;
const date = (value) => (value ? moment(value).format("DD/MM/YYYY") : "—");
const dateTime = (value) =>
  value ? moment(value).format("DD/MM/YYYY HH:mm") : "—";
const isIssued = (status) =>
  ["issued", "signed", "sent", "success"].includes(status);
const createEmptyKpiSummary = () => ({
  totalCount: 0,
  totalAmount: 0,
  issuedCount: 0,
  issuedAmount: 0,
  pendingCount: 0,
  pendingAmount: 0,
  attentionCount: 0,
  attentionAmount: 0,
  draftCount: 0,
  processingCount: 0,
  pendingStatusCount: 0,
  errorCount: 0,
  cancelledCount: 0,
  adjustmentCount: 0,
  adjustmentAmount: 0,
});

const createEmptySepayUsage = () => ({
  available: false,
  used: 0,
  total: 0,
  remaining: 0,
  isLoading: true,
  hasError: false,
});
const invoiceTypeLabel = (type) =>
  type === "adjustment"
    ? "Hóa đơn điều chỉnh"
    : type === "replacement"
      ? "Hóa đơn thay thế"
      : "Hóa đơn gốc";

const CORRECTION_INVOICE_TYPES = new Set(["adjustment", "replacement"]);

const getInvoiceKey = (value) =>
  value === null || value === undefined || value === "" ? null : String(value);

const isCorrectionInvoice = (invoice) =>
  CORRECTION_INVOICE_TYPES.has(invoice?.invoice_type);

const isReplacedInvoice = (invoice, invoices) => {
  const invoiceId = getInvoiceKey(invoice?.id);
  if (!invoiceId) return false;

  return (invoices || []).some(
    (candidate) =>
      candidate?.invoice_type === "replacement" &&
      getInvoiceKey(candidate.parent_einvoice_id) === invoiceId,
  );
};

const orderInvoiceHierarchy = (invoices) => {
  const list = Array.isArray(invoices) ? invoices : [];
  const byId = new Map(
    list
      .map((invoice) => [getInvoiceKey(invoice.id), invoice])
      .filter(([id]) => Boolean(id)),
  );
  const childrenByParent = new Map();

  list.forEach((invoice) => {
    const parentId = getInvoiceKey(invoice.parent_einvoice_id);
    if (!isCorrectionInvoice(invoice) || !parentId || !byId.has(parentId))
      return;
    const children = childrenByParent.get(parentId) || [];
    children.push(invoice);
    childrenByParent.set(parentId, children);
  });

  const ordered = [];
  const visited = new Set();
  const append = (invoice, depth) => {
    const id = getInvoiceKey(invoice.id);
    if (!id || visited.has(id)) return;
    visited.add(id);
    ordered.push({
      ...invoice,
      hierarchy_depth: depth,
    });
    (childrenByParent.get(id) || []).forEach((child) =>
      append(child, depth + 1),
    );
  };

  // Keep server ordering for roots, then place every correction/replacement
  // directly after its source document. Orphaned filtered/paginated children
  // remain visible with a child marker instead of being hidden.
  list.forEach((invoice) => {
    const parentId = getInvoiceKey(invoice.parent_einvoice_id);
    if (!isCorrectionInvoice(invoice) || !parentId || !byId.has(parentId)) {
      append(invoice, 0);
    }
  });
  list.forEach((invoice) => {
    if (!visited.has(getInvoiceKey(invoice.id))) {
      append(invoice, isCorrectionInvoice(invoice) ? 1 : 0);
    }
  });

  return ordered;
};

const cloneInvoiceItems = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => ({
    ...item,
    line_number: item.line_number || index + 1,
    item_name: item.item_name || item.name || "",
    unit: item.unit || "cái",
    quantity: Number(item.quantity ?? item.count ?? 1),
    unit_price: Number(item.unit_price ?? item.price ?? 0),
  }));
};

const ADJUSTMENT_REASON_PREFIX = Object.freeze({
  increase: "Điều chỉnh tăng đơn giá",
  decrease: "Điều chỉnh giảm đơn giá",
  combined: "Điều chỉnh tăng/giảm đơn giá",
  info: "Điều chỉnh thông tin",
});

const getDefaultAdjustmentReason = (invoice, adjustmentType = "increase") =>
  `${ADJUSTMENT_REASON_PREFIX[adjustmentType] || ADJUSTMENT_REASON_PREFIX.increase} cho hóa đơn ký hiệu ${
    invoice?.invoice_series || "—"
  } số ${invoice?.invoice_no || invoice?.id || "—"} ngày ${date(
    invoice?.invoice_date || invoice?.issued_at || invoice?.createdAt,
  )}`;

const getDefaultReplacementReason = (invoice) =>
  `Thay thế cho hóa đơn ký hiệu ${
    invoice?.invoice_series || "—"
  } số ${invoice?.invoice_no || invoice?.id || "—"} ngày ${date(
    invoice?.invoice_date || invoice?.issued_at || invoice?.createdAt,
  )}`;

const hasSePayReference = (invoice) =>
  Boolean(invoice && invoice.provider_reference_code);

const correctionEligibility = (invoice, invoices, mode) => {
  if (!invoice || invoice.provider !== "sepay") return null;
  // issuance_status is derived from the provider and is authoritative for an
  // asynchronously signed SePay document.  Older rows can still retain their
  // pre-sync local "processing" status after SePay has confirmed success.
  if (invoice.status === "cancelled" || invoice.issuance_status !== "success") {
    return "Chỉ áp dụng cho hóa đơn SePay đã phát hành thành công";
  }
  if (!hasSePayReference(invoice)) {
    return "Thiếu mã reference_code SePay của hóa đơn nguồn";
  }
  if (mode === "adjustment" && invoice.invoice_type === "replacement") {
    return "Hóa đơn thay thế chỉ có thể được thay thế tiếp";
  }
  if (
    (invoices || []).some(
      (candidate) =>
        candidate.parent_einvoice_id === invoice.id &&
        candidate.invoice_type === "replacement",
    )
  ) {
    return "Hóa đơn nguồn đã có hóa đơn thay thế trực tiếp";
  }
  return "";
};

const isEditableSepayCorrection = (invoice) =>
  invoice &&
  invoice.provider === "sepay" &&
  ["adjustment", "replacement"].includes(invoice.invoice_type) &&
  ["draft", "error"].includes(invoice.status);

const statusMeta = (status) =>
  STATUS_META[status] || { label: "Chưa xác định", color: "secondary" };

const syncMeta = (invoice) => {
  if (invoice.status === "error" || invoice.status === "cancelled") {
    return { label: "Cần kiểm tra", color: "danger" };
  }

  if (invoice.provider_invoice_id || isIssued(invoice.status)) {
    return { label: "Đã đồng bộ", color: "success" };
  }

  return { label: "Chưa đồng bộ", color: "secondary" };
};

const EInvoiceMetricCard = ({
  icon,
  tone,
  label,
  value,
  note,
  details = [],
}) => (
  <Card className="einvoice-workspace-kpi h-100">
    <CardBody>
      <div className="d-flex align-items-start">
        <div className={`einvoice-workspace-kpi-icon is-${tone}`}>
          <i className={icon} />
        </div>
        <div className="ml-3 min-width-zero">
          <div className={`einvoice-workspace-kpi-value text-${tone}`}>
            {value}
          </div>
          <div className="einvoice-workspace-kpi-label">{label}</div>
          {details.length ? (
            <div className="einvoice-workspace-kpi-breakdown">
              {details.map((detail) => (
                <span key={detail.label}>
                  <strong>{detail.value}</strong> {detail.label}
                </span>
              ))}
            </div>
          ) : (
            <small className="text-muted d-block text-truncate" title={note}>
              {note}
            </small>
          )}
        </div>
      </div>
    </CardBody>
  </Card>
);

const DetailRow = ({ label, value, danger }) => (
  <div className="einvoice-workspace-detail-row">
    <span>{label}</span>
    <strong className={danger ? "text-danger" : ""}>{value || "—"}</strong>
  </div>
);

class EInvoiceList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      currentPage: 1,
      totalPage: 1,
      selectedPageSize: 10,
      totalItem: 0,
      search: "",
      kpiSummary: createEmptyKpiSummary(),
      sepayUsage: createEmptySepayUsage(),
      isLoading: true,
      issuanceStatus: "",
      keyword: "",
      invoiceType: "",
      provider: "",
      invoiceStatus: "",
      dateStart: "",
      dateEnd: "",
      datePickerOpen: false,
      selectedInvoice: null,
      selectedIds: [],
      batchActionLoading: null,
      detailTab: "info",
      detailDrawerOpen: false,
      isCompactDesktop: false,

      selectedInvoiceId: null,

      // Email Modal
      emailModalOpen: false,
      emailAddress: "",
      emailLoading: false,
      issueLoading: false,

      // Edit Modal
      editModalOpen: false,
      editCustomerName: "",
      editCustomerTaxId: "",
      editCustomerAddress: "",
      editCustomerEmail: "",
      editCustomerPhone: "",
      editPaymentMethod: "",
      editNote: "",
      editLoading: false,

      // SePay correction / replacement modal
      correctionModalOpen: false,
      correctionMode: "adjustment",
      correctionSourceId: null,
      correctionDraftId: null,
      correctionSourceLabel: "",
      correctionSourceInvoice: null,
      correctionAdjustmentType: "increase",
      correctionReason: "",
      correctionCustomerName: "",
      correctionCustomerTaxId: "",
      correctionCustomerAddress: "",
      correctionCustomerEmail: "",
      correctionCustomerPhone: "",
      correctionPaymentMethod: "TM/CK",
      correctionNote: "",
      correctionItems: [],
      correctionVatRate: 10,
      correctionDiscountAmount: 0,
      correctionTaxFeeAmount: 0,
      correctionFeeOtherAmount: 0,
      correctionIsDraft: true,
      correctionLoading: false,
      correctionOperationalPreview: null,
      correctionOperationalPreviewLoading: false,
      operationalMapping: null,
      operationalMappingLoading: false,
      fnbRecoveryLoading: false,
      correctionProducts: [],
      correctionProductSearch: "",
      correctionSelectedProductKey: "",
      correctionProductsLoading: false,
    };
    this.scrollRef = null;
    this.operationalMappingRequestId = 0;
  }

  componentDidMount() {
    this.updateDetailPresentation();
    window.addEventListener("resize", this.updateDetailPresentation);
    this.fetchData();
    this.fetchSepayUsage();
    this.autoRefreshTimer = window.setInterval(
      this.refreshVisibleInvoiceData,
      60 * 1000,
    );
  }

  componentWillUnmount() {
    this.operationalMappingRequestId += 1;
    window.removeEventListener("resize", this.updateDetailPresentation);
    window.clearInterval(this.autoRefreshTimer);
  }

  refreshVisibleInvoiceData = () => {
    if (document.visibilityState === "visible" && !this.state.isLoading) {
      this.fetchData();
      this.fetchSepayUsage();
      if (isCorrectionInvoice(this.state.selectedInvoice)) {
        this.fetchOperationalMapping(this.state.selectedInvoice);
      }
    }
  };

  updateDetailPresentation = () => {
    const isCompactDesktop =
      window.innerWidth >= 768 && window.innerWidth < 1500;

    this.setState((previousState) => {
      if (previousState.isCompactDesktop === isCompactDesktop) return null;

      return {
        isCompactDesktop,
        detailDrawerOpen: isCompactDesktop
          ? previousState.detailDrawerOpen
          : false,
      };
    });
  };

  fetchKpiSummary = (userId, token) => {
    axios
      .get(`${END_POINT}/api/einvoice/summary?user_id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        this.setState({
          kpiSummary: { ...createEmptyKpiSummary(), ...(res.data.data || {}) },
        });
      })
      .catch((err) => {
        console.error("Get eInvoice summary error", err);
        this.setState({ kpiSummary: createEmptyKpiSummary() });
      });
  };
  fetchSepayUsage = () => {
    const userId = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");

    axios
      .get(`${END_POINT}/api/einvoice/usage?user_id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        this.setState({
          sepayUsage: {
            ...createEmptySepayUsage(),
            ...(res.data.data || {}),
            isLoading: false,
            hasError: false,
          },
        });
      })
      .catch((err) => {
        console.error("Get SePay eInvoice usage error", err);
        this.setState({
          sepayUsage: {
            ...createEmptySepayUsage(),
            isLoading: false,
            hasError: true,
          },
        });
      });
  };
  fetchData = () => {
    const { currentPage, selectedPageSize, issuanceStatus, invoiceStatus } =
      this.state;
    const user_id = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");

    this.setState({ isLoading: true });
    this.fetchKpiSummary(user_id, token);

    axios
      .get(
        `${END_POINT}/api/einvoice/list?user_id=${user_id}&page=${currentPage}&limit=${selectedPageSize}&issuance_status=${issuanceStatus}&invoice_status=${invoiceStatus}&include_hierarchy=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then((res) => {
        const items = res.data.data || [];
        this.setState(
          {
            items,
            totalItem: res.data.total || 0,
            totalPage: res.data.totalPages || 1,
            isLoading: false,
          },
          () => this.consumeOrderCorrectionLaunch(items),
        );
      })
      .catch((err) => {
        console.error(err);
        this.setState({ isLoading: false, items: [] });
      });
  };

  onChangePage = (page) => {
    this.setState({ currentPage: page }, () => this.fetchData());
  };

  openInvoicePdf = async (id, action) => {
    const user_id = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");

    const response = await axios.get(
      `${END_POINT}/api/einvoice/${id}/${action}?user_id=${user_id}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      },
    );
    const blobUrl = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/pdf" }),
    );

    if (action === "download") {
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `einvoice-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      return;
    }

    window.open(blobUrl, "_blank");
  };

  handleViewPdf = (id) => {
    this.openInvoicePdf(id, "pdf").catch((err) => {
      console.error(err);
      NotificatioErrController("Không thể mở hóa đơn điện tử");
    });
  };

  handleDownloadPdf = (id) => {
    this.openInvoicePdf(id, "download").catch((err) => {
      console.error(err);
      NotificatioErrController("Không thể tải hóa đơn điện tử");
    });
  };

  getBatchDownloadErrorMessage = async (err) => {
    const payload = err.response?.data;
    if (payload && typeof payload.text === "function") {
      try {
        const text = await payload.text();
        const parsed = JSON.parse(text);
        return (
          parsed.message ||
          parsed.responseText ||
          "Không thể tạo tệp PDF hàng loạt"
        );
      } catch (_error) {
        return "Không thể tạo tệp PDF hàng loạt";
      }
    }

    return (
      payload?.message ||
      payload?.responseText ||
      err.message ||
      "Không thể tạo tệp PDF hàng loạt"
    );
  };

  handleBatchDownloadPdf = async () => {
    const { selectedIds } = this.state;
    if (!selectedIds.length) {
      NotificatioErrController("Vui lòng chọn ít nhất một hóa đơn");
      return;
    }
    if (selectedIds.length > 20) {
      NotificatioErrController("Chỉ có thể tải tối đa 20 hóa đơn mỗi lần");
      return;
    }

    const user_id = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");
    this.setState({ batchActionLoading: "download" });

    try {
      const response = await axios.post(
        `${END_POINT}/api/einvoice/batch/download-pdf`,
        { user_id, invoice_ids: selectedIds },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
          timeout: 120000,
        },
      );
      const contentDisposition =
        response.headers?.["content-disposition"] || "";
      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/zip" }),
      );
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filenameMatch?.[1] || "einvoices.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      NotificatioSucController(
        "Đã tạo tệp ZIP. Xem ket-qua.txt trong tệp nếu có hóa đơn không tải được.",
      );
    } catch (err) {
      console.error("Batch e-invoice PDF download error", err);
      NotificatioErrController(await this.getBatchDownloadErrorMessage(err));
    } finally {
      this.setState({ batchActionLoading: null });
    }
  };

  handleBatchSendEmail = async () => {
    const { selectedIds } = this.state;
    if (!selectedIds.length) {
      NotificatioErrController("Vui lòng chọn ít nhất một hóa đơn");
      return;
    }
    if (selectedIds.length > 20) {
      NotificatioErrController("Chỉ có thể gửi tối đa 20 hóa đơn mỗi lần");
      return;
    }
    if (
      !window.confirm(
        `Gửi email hóa đơn đến email khách hàng đã lưu cho ${selectedIds.length} hóa đơn đã chọn?`,
      )
    ) {
      return;
    }

    const user_id = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");
    this.setState({ batchActionLoading: "email" });

    try {
      const response = await axios.post(
        `${END_POINT}/api/einvoice/batch/send-email`,
        { user_id, invoice_ids: selectedIds },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000,
        },
      );
      const result = response.data?.data || {};
      if (result.sent) {
        NotificatioSucController(
          `Đã gửi email ${result.sent}/${result.total} hóa đơn`,
        );
      }
      if (result.skipped || result.failed) {
        NotificatioErrController(
          `${result.skipped || 0} hóa đơn bị bỏ qua, ${result.failed || 0} hóa đơn gửi lỗi. Kiểm tra email khách hàng và trạng thái phát hành.`,
        );
      }
      this.fetchData();
    } catch (err) {
      console.error("Batch e-invoice email error", err);
      NotificatioErrController(
        err.response?.data?.message ||
          err.response?.data?.responseText ||
          "Không thể gửi email hàng loạt",
      );
    } finally {
      this.setState({ batchActionLoading: null });
    }
  };

  handleCheckStatus = (id) => {
    const user_id = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");

    axios
      .get(`${END_POINT}/api/einvoice/${id}/status?user_id=${user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        NotificatioSucController(
          this.props.intl.formatMessage({
            id: "einvoice.notification.status-updated",
          }),
        );
        this.fetchData();
      })
      .catch((err) => {
        NotificatioErrController(
          err.response?.data?.responseText || "Lỗi cập nhật",
        );
      });
  };

  // Email Modal
  openEmailModal = (item) => {
    this.setState({
      emailModalOpen: true,
      selectedInvoiceId: item.id,
      emailAddress: item.customer_email || "",
    });
  };

  closeEmailModal = () => {
    this.setState({
      emailModalOpen: false,
      selectedInvoiceId: null,
      emailAddress: "",
    });
  };

  handleSendEmail = () => {
    const { selectedInvoiceId, emailAddress } = this.state;
    const user_id = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");

    if (!emailAddress) {
      NotificatioErrController(
        this.props.intl.formatMessage({
          id: "einvoice.notification.email-required",
        }),
      );
      return;
    }

    this.setState({ emailLoading: true });

    axios
      .post(
        `${END_POINT}/api/einvoice/${selectedInvoiceId}/send-email`,
        {
          user_id,
          email: emailAddress,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then((res) => {
        NotificatioSucController(
          this.props.intl.formatMessage({
            id: "einvoice.alert.email-success",
          }),
        );
        this.closeEmailModal();
        this.fetchData();
      })
      .catch((err) => {
        NotificatioErrController(
          err.response?.data?.responseText || "Lỗi gửi email",
        );
      })
      .finally(() => {
        this.setState({ emailLoading: false });
      });
  };

  handleIssueOfficial = (item) => {
    const user_id = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");

    this.setState({ issueLoading: true });

    axios
      .post(
        `${END_POINT}/api/einvoice/${item.id}/issue-official`,
        { user_id },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => {
        NotificatioSucController(
          this.props.intl.formatMessage({
            id: "einvoice.notification.issue-official-success",
          }),
        );
        this.fetchData();
        this.fetchSepayUsage();
      })
      .catch((err) => {
        NotificatioErrController(
          err.response?.data?.responseText || "Lỗi phát hành hóa đơn",
        );
      })
      .finally(() => {
        this.setState({ issueLoading: false });
      });
  };

  openEditModal = (item) => {
    this.setState({
      editModalOpen: true,
      selectedInvoiceId: item.id,
      editCustomerName: item.customer_name || "",
      editCustomerTaxId: item.customer_tax_id || "",
      editCustomerAddress: item.customer_address || "",
      editCustomerEmail: item.customer_email || "",
      editCustomerPhone: item.customer_phone || "",
      editPaymentMethod: item.payment_method || "TM/CK",
      editNote: item.note || "",
    });
  };

  closeEditModal = () => {
    this.setState({
      editModalOpen: false,
      selectedInvoiceId: null,
      editCustomerName: "",
      editCustomerTaxId: "",
      editCustomerAddress: "",
      editCustomerEmail: "",
      editCustomerPhone: "",
      editPaymentMethod: "",
      editNote: "",
    });
  };

  handleEditInvoice = () => {
    const {
      selectedInvoiceId,
      editCustomerName,
      editCustomerTaxId,
      editCustomerAddress,
      editCustomerEmail,
      editCustomerPhone,
      editPaymentMethod,
      editNote,
    } = this.state;
    const user_id = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");

    if (!editCustomerName) {
      NotificatioErrController(
        this.props.intl.formatMessage({
          id: "einvoice.notification.customer-name-required",
        }),
      );
      return;
    }

    this.setState({ editLoading: true });

    axios
      .put(
        `${END_POINT}/api/einvoice/${selectedInvoiceId}`,
        {
          user_id,
          customer_name: editCustomerName,
          customer_tax_id: editCustomerTaxId,
          customer_address: editCustomerAddress,
          customer_email: editCustomerEmail,
          customer_phone: editCustomerPhone,
          payment_method: editPaymentMethod,
          note: editNote,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then((res) => {
        NotificatioSucController(
          this.props.intl.formatMessage({
            id: "einvoice.notification.update-success",
          }),
        );
        this.closeEditModal();
        this.fetchData();
      })
      .catch((err) => {
        NotificatioErrController(
          err.response?.data?.responseText || "Lỗi cập nhật hóa đơn",
        );
      })
      .finally(() => {
        this.setState({ editLoading: false });
      });
  };

  openCorrectionModal = (invoice, requestedMode) => {
    const isExistingDraft = isEditableSepayCorrection(invoice);
    const mode = requestedMode || invoice.invoice_type || "adjustment";
    const isReplacement = mode === "replacement";
    const sourceInvoice = isExistingDraft
      ? this.state.items.find(
          (item) =>
            getInvoiceKey(item.id) ===
            getInvoiceKey(invoice.parent_einvoice_id),
        ) || invoice
      : invoice;
    const adjustmentType =
      invoice.adjustment_direction === "mixed"
        ? "combined"
        : invoice.adjustment_direction || "increase";
    const snapshot = isExistingDraft
      ? invoice.target_snapshot || invoice.source_snapshot || invoice
      : invoice;
    this.setState(
      {
        correctionModalOpen: true,
        correctionMode: mode,
        correctionSourceId: isExistingDraft
          ? invoice.parent_einvoice_id
          : invoice.id,
        correctionDraftId: isExistingDraft ? invoice.id : null,
        correctionSourceLabel: isExistingDraft
          ? invoice.parent_einvoice_id
          : invoice.invoice_no || invoice.id,
        correctionSourceInvoice: sourceInvoice,
        correctionAdjustmentType: adjustmentType,
        correctionReason: isExistingDraft
          ? invoice.adjustment_reason || ""
          : mode === "adjustment"
            ? getDefaultAdjustmentReason(sourceInvoice, adjustmentType)
            : mode === "replacement"
              ? getDefaultReplacementReason(sourceInvoice)
              : invoice.adjustment_reason || "",
        correctionCustomerName: invoice.customer_name || "",
        correctionCustomerTaxId: invoice.customer_tax_id || "",
        correctionCustomerAddress: invoice.customer_address || "",
        correctionCustomerEmail: invoice.customer_email || "",
        correctionCustomerPhone: invoice.customer_phone || "",
        correctionPaymentMethod: invoice.payment_method || "TM/CK",
        correctionNote: invoice.note || "",
        correctionItems: cloneInvoiceItems(snapshot.items),
        correctionVatRate: Number(snapshot.vat_rate ?? invoice.vat_rate ?? 10),
        // The replacement request only sends item lines and their tax to SePay.
        // Do not silently inherit source-level discount/fee amounts which would
        // otherwise make our locally calculated total diverge from SePay.
        correctionDiscountAmount: isReplacement
          ? 0
          : Number(snapshot.discount_amount ?? invoice.discount_amount ?? 0),
        correctionTaxFeeAmount: isReplacement
          ? 0
          : Number(snapshot.tax_fee_amount ?? invoice.tax_fee_amount ?? 0),
        correctionFeeOtherAmount: isReplacement
          ? 0
          : Number(snapshot.fee_other_amount ?? invoice.fee_other_amount ?? 0),
        correctionIsDraft: true,
        correctionOperationalPreview: null,
        correctionProductSearch: "",
        correctionSelectedProductKey: "",
      },
      () => {
        if (isExistingDraft) this.fetchCorrectionOperationalPreview(invoice.id);
        this.fetchCorrectionProducts("");
      },
    );
  };

  closeCorrectionModal = () => {
    this.setState({
      correctionModalOpen: false,
      correctionSourceId: null,
      correctionDraftId: null,
      correctionSourceInvoice: null,
      correctionReason: "",
      correctionItems: [],
      correctionLoading: false,
      correctionOperationalPreview: null,
      correctionOperationalPreviewLoading: false,
      correctionProducts: [],
      correctionProductSearch: "",
      correctionSelectedProductKey: "",
      correctionProductsLoading: false,
    });
  };

  consumeOrderCorrectionLaunch = (items) => {
    let launch;
    try {
      launch = JSON.parse(
        sessionStorage.getItem("vmass:einvoice-correction-launch") || "null",
      );
    } catch (_error) {
      sessionStorage.removeItem("vmass:einvoice-correction-launch");
      return;
    }
    if (!launch?.invoiceId) return;
    const invoice = (items || []).find(
      (item) => Number(item.id) === Number(launch.invoiceId),
    );
    if (!invoice) return;
    sessionStorage.removeItem("vmass:einvoice-correction-launch");
    this.openCorrectionModal(
      invoice,
      launch.mode === "replacement" ? "replacement" : "adjustment",
    );
  };

  fetchCorrectionOperationalPreview = async (invoiceId) => {
    const userId = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");
    this.setState({ correctionOperationalPreviewLoading: true });
    try {
      const response = await axios.get(
        `${END_POINT}/api/einvoice/${invoiceId}/operational-mapping-preview?user_id=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      this.setState({
        correctionOperationalPreview: response.data?.data || null,
      });
    } catch (error) {
      this.setState({ correctionOperationalPreview: null });
    } finally {
      this.setState({ correctionOperationalPreviewLoading: false });
    }
  };

  fetchOperationalMapping = async (invoice) => {
    const requestId = ++this.operationalMappingRequestId;
    const invoiceKey = getInvoiceKey(invoice?.id);
    if (!isCorrectionInvoice(invoice)) {
      this.setState({
        operationalMapping: null,
        operationalMappingLoading: false,
      });
      return;
    }
    const userId = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");
    this.setState({
      operationalMappingLoading: true,
      operationalMapping: null,
    });
    try {
      const response = await axios.get(
        `${END_POINT}/api/einvoice/${invoice.id}/operational-mapping?user_id=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const mapping = response.data?.data || null;
      if (
        requestId === this.operationalMappingRequestId &&
        getInvoiceKey(this.state.selectedInvoice?.id) === invoiceKey
      ) {
        this.setState({
          operationalMapping:
            getInvoiceKey(mapping?.einvoice_id) === invoiceKey ? mapping : null,
        });
      }
    } catch (_error) {
      if (requestId === this.operationalMappingRequestId) {
        this.setState({ operationalMapping: null });
      }
    } finally {
      if (requestId === this.operationalMappingRequestId) {
        this.setState({ operationalMappingLoading: false });
      }
    }
  };

  recoverFnbRecipeIngredients = async (invoice) => {
    if (!invoice || this.state.fnbRecoveryLoading) return;
    const confirmed = window.confirm(
      "Chỉ dùng khi hóa đơn đã điều chỉnh giảm món F&B nhưng nguyên liệu chưa được hồi. Thao tác chỉ khôi phục nguyên liệu theo định lượng, không chạy lại bill, thanh toán hoặc hóa đơn. Tiếp tục?",
    );
    if (!confirmed) return;

    const userId = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");
    this.setState({ fnbRecoveryLoading: true });
    try {
      const response = await axios.post(
        `${END_POINT}/api/einvoice/${invoice.id}/operational-mapping/recover-fnb-ingredients`,
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      NotificatioSucController(
        response.data?.message ||
          "Đã khôi phục nguyên liệu F&B theo công thức định lượng",
      );
      const mapping = response.data?.data || null;
      if (
        getInvoiceKey(mapping?.einvoice_id) === getInvoiceKey(invoice.id) &&
        getInvoiceKey(this.state.selectedInvoice?.id) ===
          getInvoiceKey(invoice.id)
      ) {
        this.setState({ operationalMapping: mapping });
      }
      this.fetchData();
    } catch (error) {
      NotificatioErrController(
        error?.response?.data?.responseText ||
          "Không thể khôi phục nguyên liệu F&B",
      );
    } finally {
      this.setState({ fnbRecoveryLoading: false });
    }
  };

  updateCorrectionItem = (index, field, value) => {
    this.setState((previous) => ({
      correctionItems: previous.correctionItems.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [field]: ["quantity", "unit_price"].includes(field)
                ? Number(value)
                : value,
            }
          : item,
      ),
    }));
  };

  getCorrectionProductRows = (products = this.state.correctionProducts) =>
    (products || []).flatMap((product) => {
      const variants =
        product?.variants || product?.variant || product?.ProductVariants || [];
      if (!variants.length) {
        return [
          {
            key: `${product.id}:`,
            product,
            variant: null,
            label: product.name,
          },
        ];
      }
      return variants.map((variant) => ({
        key: `${product.id}:${variant.id}`,
        product,
        variant,
        label: `${product.name} - ${variant.name || variant.sku || `Biến thể ${variant.id}`}`,
      }));
    });

  fetchCorrectionProducts = async (
    search = this.state.correctionProductSearch,
  ) => {
    const token = localStorage.getItem("access_token");
    this.setState({ correctionProductsLoading: true });
    try {
      const response = await axios.get(
        `${END_POINT}/product/json?pageSize=30&currentPage=1&search=${encodeURIComponent(search || "")}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const correctionProducts = response.data?.data || [];
      const rows = this.getCorrectionProductRows(correctionProducts);
      this.setState((previous) => ({
        correctionProducts,
        correctionSelectedProductKey: rows.some(
          (row) => row.key === previous.correctionSelectedProductKey,
        )
          ? previous.correctionSelectedProductKey
          : rows[0]?.key || "",
      }));
    } catch (_error) {
      this.setState({
        correctionProducts: [],
        correctionSelectedProductKey: "",
      });
    } finally {
      this.setState({ correctionProductsLoading: false });
    }
  };

  addCorrectionItem = () => {
    const selected = this.getCorrectionProductRows().find(
      (row) => row.key === this.state.correctionSelectedProductKey,
    );
    if (!selected) {
      NotificatioErrController(
        "Vui lòng chọn sản phẩm hoặc biến thể cần thêm.",
      );
      return;
    }
    const attributes = selected.variant?.attributes;
    let parsedAttributes = {};
    try {
      parsedAttributes =
        typeof attributes === "string"
          ? JSON.parse(attributes)
          : attributes || {};
    } catch (_error) {
      parsedAttributes = {};
    }
    const price = Number(
      selected.variant?.price ||
        (selected.product?.active_sale && selected.product?.price_sale) ||
        selected.product?.price ||
        0,
    );
    const newItem = {
      product_id: selected.product.id,
      product_variant_id: selected.variant?.id || null,
      order_ids: [],
      item_code:
        selected.variant?.sku ||
        selected.product?.sku ||
        `SP${selected.product.id}`,
      item_name: selected.product.name || "Sản phẩm",
      unit: selected.product.sale_unit || "cái",
      quantity: 1,
      unit_price: price,
      size: parsedAttributes.size || parsedAttributes.Size || "",
      flavor: parsedAttributes.flavor || parsedAttributes.Flavor || "",
      topping: parsedAttributes.topping || parsedAttributes.Topping || "",
      note: "",
    };
    this.setState((previous) => ({
      correctionItems: [
        ...previous.correctionItems,
        { ...newItem, line_number: previous.correctionItems.length + 1 },
      ],
    }));
  };

  removeCorrectionItem = (index) => {
    this.setState((previous) => ({
      correctionItems: previous.correctionItems
        .filter((_, itemIndex) => itemIndex !== index)
        .map((item, itemIndex) => ({ ...item, line_number: itemIndex + 1 })),
    }));
  };

  handleSubmitCorrection = async () => {
    const {
      correctionMode,
      correctionSourceId,
      correctionDraftId,
      correctionAdjustmentType,
      correctionReason,
      correctionCustomerName,
      correctionCustomerTaxId,
      correctionCustomerAddress,
      correctionCustomerEmail,
      correctionCustomerPhone,
      correctionPaymentMethod,
      correctionNote,
      correctionItems,
      correctionVatRate,
      correctionDiscountAmount,
      correctionTaxFeeAmount,
      correctionFeeOtherAmount,
      correctionIsDraft,
    } = this.state;
    const user_id = localStorage.getItem("user_id");
    const token = localStorage.getItem("access_token");

    if (!String(correctionReason || "").trim()) {
      NotificatioErrController("Vui lòng nhập lý do điều chỉnh/thay thế");
      return;
    }
    if (
      (correctionMode === "replacement" ||
        correctionAdjustmentType === "info") &&
      !String(correctionCustomerName || "").trim()
    ) {
      NotificatioErrController("Vui lòng nhập tên người mua");
      return;
    }
    if (correctionAdjustmentType !== "info" && !correctionItems.length) {
      NotificatioErrController(
        "Vui lòng kiểm tra tên hàng, số lượng và đơn giá",
      );
      return;
    }
    if (
      correctionAdjustmentType !== "info" &&
      correctionItems.some(
        (item) =>
          !Number.isFinite(Number(item.quantity)) ||
          Number(item.quantity) < 0 ||
          !Number.isInteger(Number(item.quantity)),
      )
    ) {
      NotificatioErrController("Số lượng sản phẩm phải là số nguyên không âm");
      return;
    }
    if (
      correctionAdjustmentType !== "info" &&
      correctionItems.some(
        (item) =>
          !String(item.item_name || "").trim() ||
          !Number.isFinite(Number(item.unit_price)) ||
          Number(item.unit_price) < 0,
      )
    ) {
      NotificatioErrController("Vui lòng kiểm tra tên hàng và đơn giá");
      return;
    }

    const payload = {
      user_id,
      adjustment_type: correctionAdjustmentType,
      adjustment_reason: correctionReason,
      reason: correctionReason,
      customer_name: correctionCustomerName,
      customer_tax_id: correctionCustomerTaxId,
      customer_address: correctionCustomerAddress,
      customer_email: correctionCustomerEmail,
      customer_phone: correctionCustomerPhone,
      payment_method: correctionPaymentMethod,
      note: correctionNote,
      items: correctionItems,
      vat_rate: correctionVatRate,
      discount_amount:
        correctionMode === "replacement" ? 0 : correctionDiscountAmount,
      tax_fee_amount:
        correctionMode === "replacement" ? 0 : correctionTaxFeeAmount,
      fee_other_amount:
        correctionMode === "replacement" ? 0 : correctionFeeOtherAmount,
      is_draft: correctionIsDraft,
    };
    // console.log("payload: ", payload);

    this.setState({ correctionLoading: true });
    try {
      let response;
      if (correctionDraftId) {
        await axios.put(
          `${END_POINT}/api/einvoice/${correctionDraftId}/correction-draft`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        response = await axios.post(
          `${END_POINT}/api/einvoice/${correctionDraftId}/correction-draft/submit`,
          { user_id, is_draft: correctionIsDraft },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      } else {
        const suffix =
          correctionMode === "replacement" ? "replacements" : "adjustments";
        response = await axios.post(
          `${END_POINT}/api/einvoice/${correctionSourceId}/${suffix}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
      // console.log("response: ", response.data);
      const trackingCode = response.data?.tracking_code;
      const operationalMapping = response.data?.operational_mapping || null;
      NotificatioSucController(
        trackingCode
          ? `Đã gửi SePay. Mã theo dõi: ${trackingCode}`
          : "Đã gửi yêu cầu hóa đơn đến SePay",
      );
      if (operationalMapping?.state === "applied") {
        NotificatioSucController(
          "VMASS đã đồng bộ bill, tồn kho và chênh lệch thanh toán.",
        );
      }
      this.closeCorrectionModal();
      this.fetchData();
      this.fetchSepayUsage();
    } catch (error) {
      NotificatioErrController(
        error.response?.data?.responseText ||
          "Không thể gửi hóa đơn điều chỉnh/thay thế",
      );
    } finally {
      this.setState({ correctionLoading: false });
    }
  };

  renderLegacy() {
    const {
      items,
      isLoading,
      currentPage,
      totalPage,
      emailModalOpen,
      emailAddress,
      emailLoading,
      editModalOpen,
      editLoading,
    } = this.state;
    const { intl } = this.props;

    return (
      <Row>
        <Colxx xxs="12">
          {isLoading ? (
            <div className="loading" />
          ) : (
            <>
              <div className="mb-2">
                <Button
                  color="primary"
                  size="sm"
                  className="mb-2"
                  onClick={() => this.fetchData()}
                >
                  <i className="simple-icon-refresh mr-1"></i>
                  <IntlMessages id="button.refresh" />
                </Button>
              </div>
              <div className="table-responsive">
                <table
                  className="table table-hover"
                  style={{ whiteSpace: "nowrap" }}
                >
                  <thead>
                    <tr className="text-center">
                      <th>
                        <IntlMessages id="table.invoice-no" />
                      </th>
                      <th>
                        <IntlMessages id="table.customer" />
                      </th>
                      <th>
                        <IntlMessages id="table.phone" />
                      </th>
                      <th>
                        <IntlMessages id="table.address" />
                      </th>
                      <th>
                        <IntlMessages id="table.email" />
                      </th>
                      <th>
                        <IntlMessages id="table.date" />
                      </th>
                      <th>
                        <IntlMessages id="table.total" />
                      </th>
                      <th>
                        <IntlMessages id="table.discount" />
                      </th>
                      <th>
                        <IntlMessages id="table.payment_method" />
                      </th>
                      <th>
                        <IntlMessages id="table.note" />
                      </th>
                      <th>
                        <IntlMessages id="table.status" />
                      </th>
                      <th>
                        <IntlMessages id="table.action" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id} className="text-center">
                        <td style={{ verticalAlign: "middle" }}>
                          <strong>{item.invoice_no || "---"}</strong>
                          <br />
                          <small className="text-muted">
                            {item.invoice_series}
                          </small>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {item.customer_name}
                          <br />
                          <small className="text-muted">
                            {item.customer_tax_id}
                          </small>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {item.customer_phone}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <span title={item.customer_address}>
                            {item.customer_address &&
                            item.customer_address.length > 30
                              ? item.customer_address.substring(0, 30) + "..."
                              : item.customer_address}
                          </span>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {item.customer_email}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {moment(item.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {AddCommaNumber(Math.round(item.total_amount))}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {AddCommaNumber(
                            Math.round(item.discount_amount || 0),
                          )}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          {item.payment_method}
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <span title={item.note}>
                            {item.note && item.note.length > 20
                              ? item.note.substring(0, 20) + "..."
                              : item.note}
                          </span>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <Badge
                            color={
                              item.status === "issued" ||
                              item.status === "signed" ||
                              item.status === "sent"
                                ? "success"
                                : item.status === "cancelled"
                                  ? "danger"
                                  : item.status === "error"
                                    ? "warning"
                                    : "secondary"
                            }
                            className="order-dashboard-einvoice-badge"
                            title={
                              item.status === "error"
                                ? item.error_message
                                : undefined
                            }
                            style={
                              item.status === "error"
                                ? { cursor: "help" }
                                : undefined
                            }
                          >
                            <IntlMessages
                              id={`einvoice.status.${item.status}`}
                            />
                          </Badge>
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <UncontrolledDropdown>
                            <DropdownToggle
                              caret
                              color="primary"
                              outline
                              size="xs"
                            >
                              <IntlMessages id="table.action" />
                            </DropdownToggle>
                            <DropdownMenu>
                              {(item.status === "draft" ||
                                item.status === "error") && (
                                <>
                                  <DropdownItem
                                    onClick={() =>
                                      this.handleIssueOfficial(item)
                                    }
                                  >
                                    <i className="simple-icon-check mr-2" />
                                    <IntlMessages
                                      id={
                                        item.status === "error"
                                          ? "einvoice.action.reissue"
                                          : "einvoice.action.issue-official"
                                      }
                                    />
                                  </DropdownItem>
                                  <DropdownItem
                                    onClick={() => this.openEditModal(item)}
                                  >
                                    <i className="simple-icon-note mr-2" />
                                    <IntlMessages id="einvoice.action.edit" />
                                  </DropdownItem>
                                </>
                              )}
                              {item.status !== "draft" && (
                                <DropdownItem
                                  onClick={() => this.handleViewPdf(item.id)}
                                >
                                  <i className="simple-icon-doc mr-2" />
                                  <IntlMessages id="einvoice.action.view-pdf" />
                                </DropdownItem>
                              )}
                              {item.status !== "draft" && (
                                <DropdownItem
                                  onClick={() =>
                                    this.handleDownloadPdf(item.id)
                                  }
                                >
                                  <i className="simple-icon-cloud-download mr-2" />
                                  <IntlMessages id="einvoice.action.download-pdf" />
                                </DropdownItem>
                              )}
                              {item.status !== "draft" &&
                                item.status !== "cancelled" && (
                                  <DropdownItem
                                    onClick={() => this.openEmailModal(item)}
                                  >
                                    <i className="simple-icon-envelope mr-2" />
                                    <IntlMessages id="einvoice.action.send-email" />
                                  </DropdownItem>
                                )}
                              <DropdownItem
                                onClick={() => this.handleCheckStatus(item.id)}
                              >
                                <i className="simple-icon-refresh mr-2" />
                                <IntlMessages id="einvoice.action.check-status" />
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">
                          <IntlMessages id="table.no-data" />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={currentPage}
                totalPage={totalPage}
                onChangePage={this.onChangePage}
              />
            </>
          )}
        </Colxx>

        {/* Email Modal */}
        <Modal isOpen={emailModalOpen} toggle={this.closeEmailModal}>
          <ModalHeader toggle={this.closeEmailModal}>
            <IntlMessages id="einvoice.action.send-email" />
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>
                <IntlMessages id="einvoice.email.placeholder" />
              </Label>
              <Input
                type="email"
                value={emailAddress}
                onChange={(e) =>
                  this.setState({ emailAddress: e.target.value })
                }
                placeholder="email@example.com"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={this.closeEmailModal}>
              <IntlMessages id="button.cancel" />
            </Button>
            <Button
              color="primary"
              onClick={this.handleSendEmail}
              disabled={emailLoading}
            >
              {emailLoading ? (
                <span className="spinner-border spinner-border-sm mr-1" />
              ) : (
                <i className="simple-icon-envelope mr-1" />
              )}
              <IntlMessages id="einvoice.action.send-email" />
            </Button>
          </ModalFooter>
        </Modal>

        {/* Edit Modal */}
        <Modal isOpen={editModalOpen} toggle={this.closeEditModal}>
          <ModalHeader toggle={this.closeEditModal}>
            <IntlMessages id="einvoice.edit.title" />
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>
                <IntlMessages id="einvoice.edit.customer-name" />
              </Label>
              <Input
                type="text"
                value={this.state.editCustomerName}
                onChange={(e) =>
                  this.setState({ editCustomerName: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "einvoice.edit.placeholder.customer-name",
                })}
              />
            </FormGroup>
            <FormGroup>
              <Label>
                <IntlMessages id="settings.einvoice.tax-id" />
              </Label>
              <Input
                type="text"
                value={this.state.editCustomerTaxId}
                onChange={(e) =>
                  this.setState({ editCustomerTaxId: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "einvoice.edit.placeholder.tax-id",
                })}
              />
            </FormGroup>
            <FormGroup>
              <Label>
                <IntlMessages id="settings.einvoice.address" />
              </Label>
              <Input
                type="textarea"
                rows={2}
                value={this.state.editCustomerAddress}
                onChange={(e) =>
                  this.setState({ editCustomerAddress: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "einvoice.edit.placeholder.address",
                })}
              />
            </FormGroup>
            <FormGroup>
              <Label>
                <IntlMessages id="einvoice.edit.email" />
              </Label>
              <Input
                type="email"
                value={this.state.editCustomerEmail}
                onChange={(e) =>
                  this.setState({ editCustomerEmail: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "einvoice.edit.placeholder.email",
                })}
              />
            </FormGroup>
            <FormGroup>
              <Label>
                <IntlMessages id="settings.einvoice.phone" />
              </Label>
              <Input
                type="text"
                value={this.state.editCustomerPhone}
                onChange={(e) =>
                  this.setState({ editCustomerPhone: e.target.value })
                }
                placeholder={intl.formatMessage({
                  id: "einvoice.edit.placeholder.phone",
                })}
              />
            </FormGroup>
            <FormGroup>
              <Label>
                <IntlMessages id="einvoice.edit.payment-method" />
              </Label>
              <Input
                type="select"
                value={this.state.editPaymentMethod}
                onChange={(e) =>
                  this.setState({ editPaymentMethod: e.target.value })
                }
              >
                <option value="TM/CK">TM/CK (Tiền mặt / Chuyển khoản)</option>
                <option value="TM">TM (Tiền mặt)</option>
                <option value="CK">CK (Chuyển khoản)</option>
                <option value="KHAC">KHAC (Khác)</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>
                <IntlMessages id="einvoice.edit.note" />
              </Label>
              <Input
                type="textarea"
                rows={2}
                value={this.state.editNote}
                onChange={(e) => this.setState({ editNote: e.target.value })}
                placeholder={intl.formatMessage({
                  id: "einvoice.edit.placeholder.note",
                })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={this.closeEditModal}>
              <IntlMessages id="button.cancel" />
            </Button>
            <Button
              color="primary"
              onClick={this.handleEditInvoice}
              disabled={editLoading}
            >
              {editLoading && (
                <span className="spinner-border spinner-border-sm mr-1" />
              )}
              <IntlMessages id="button.save-changes" />
            </Button>
          </ModalFooter>
        </Modal>
      </Row>
    );
  }
  setServerFilter = (field, value) => {
    this.setState({ [field]: value, currentPage: 1 }, this.fetchData);
  };

  setClientFilter = (field, value) => {
    this.setState({ [field]: value });
  };

  handleInvoiceDateChange = (selectedDate) => {
    if (!selectedDate) {
      this.setState(
        {
          dateStart: "",
          dateEnd: "",
          datePickerOpen: false,
          currentPage: 1,
        },
        this.fetchData,
      );
      return;
    }

    const selectedValue = moment(selectedDate).format("YYYY-MM-DD");
    const { dateStart, dateEnd } = this.state;

    if (!dateStart || dateEnd) {
      this.setState({
        dateStart: selectedValue,
        dateEnd: "",
        datePickerOpen: true,
      });
      return;
    }

    const selectedBeforeStart = moment(selectedValue).isBefore(
      dateStart,
      "day",
    );
    this.setState(
      {
        dateStart: selectedBeforeStart ? selectedValue : dateStart,
        dateEnd: selectedBeforeStart ? dateStart : selectedValue,
        datePickerOpen: false,
        currentPage: 1,
      },
      this.fetchData,
    );
  };

  clearFilters = () => {
    this.setState(
      {
        issuanceStatus: "",
        keyword: "",
        invoiceType: "",
        provider: "",
        invoiceStatus: "",
        dateStart: "",
        dateEnd: "",
        datePickerOpen: false,
        currentPage: 1,
      },
      this.fetchData,
    );
  };

  changePageSize = (event) => {
    this.setState(
      { selectedPageSize: Number(event.target.value), currentPage: 1 },
      this.fetchData,
    );
  };

  getVisibleItems = () => {
    const { items, keyword, invoiceType, provider, dateStart, dateEnd } =
      this.state;
    const normalizedKeyword = keyword.trim().toLocaleLowerCase("vi");

    const filteredItems = items.filter((invoice) => {
      const invoiceDate = invoice.invoice_date || invoice.createdAt;
      const normalizedDate = invoiceDate
        ? moment(invoiceDate).format("YYYY-MM-DD")
        : "";
      const searchable = [
        invoice.invoice_no,
        invoice.invoice_series,
        invoice.customer_name,
        invoice.customer_tax_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("vi");

      return (
        (!normalizedKeyword || searchable.includes(normalizedKeyword)) &&
        (!invoiceType || invoice.invoice_type === invoiceType) &&
        (!provider || invoice.provider === provider) &&
        (!dateStart || normalizedDate >= dateStart) &&
        (!dateEnd || normalizedDate <= dateEnd)
      );
    });

    return orderInvoiceHierarchy(filteredItems);
  };

  selectInvoice = (invoice) => {
    this.setState(
      (previousState) => ({
        selectedInvoice: invoice,
        detailTab: "info",
        detailDrawerOpen: previousState.isCompactDesktop,
      }),
      () => this.fetchOperationalMapping(invoice),
    );
  };

  toggleSelection = (id) => {
    this.setState((previousState) => ({
      selectedIds: previousState.selectedIds.includes(id)
        ? previousState.selectedIds.filter((selectedId) => selectedId !== id)
        : [...previousState.selectedIds, id],
    }));
  };

  toggleAll = (visibleItems) => {
    const { selectedIds } = this.state;
    const itemIds = visibleItems.map((invoice) => invoice.id);
    const hasSelectedAll =
      itemIds.length > 0 && itemIds.every((id) => selectedIds.includes(id));

    this.setState({
      selectedIds: hasSelectedAll
        ? selectedIds.filter((id) => !itemIds.includes(id))
        : Array.from(new Set([...selectedIds, ...itemIds])),
    });
  };

  exportInvoices = (visibleItems) => {
    const quote = (value) => `"${String(value || "").replace(/"/g, '""')}"`;
    const rows = visibleItems.map((invoice) => [
      invoice.invoice_no || "",
      invoice.invoice_series || "",
      date(invoice.invoice_date || invoice.createdAt),
      invoice.customer_name || "",
      invoice.customer_tax_id || "",
      Number(invoice.total_amount || 0),
      statusMeta(invoice.status).label,
      syncMeta(invoice).label,
      invoiceTypeLabel(invoice.invoice_type),
    ]);
    const csv = [
      [
        "Số hóa đơn",
        "Ký hiệu",
        "Ngày lập",
        "Khách hàng",
        "Mã số thuế",
        "Tổng tiền (VND)",
        this.props.intl.formatMessage({
          id: "einvoice.filter.issuance-status",
        }),
        "Trạng thái đồng bộ",
        "Loại hóa đơn",
      ],
      ...rows,
    ]
      .map((row) => row.map(quote).join(","))
      .join("\r\n");
    const url = window.URL.createObjectURL(
      new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `hoa-don-dien-tu-${moment().format("YYYY-MM-DD")}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  render() {
    const {
      isLoading,
      totalItem,
      kpiSummary,
      sepayUsage,
      currentPage,
      totalPage,
      selectedPageSize,
      selectedIds,
      batchActionLoading,
      selectedInvoice,
      issuanceStatus,
      invoiceType,
      invoiceStatus,
      dateStart,
      dateEnd,
      datePickerOpen,
      detailTab,
      detailDrawerOpen,
      isCompactDesktop,
      issueLoading,
      correctionModalOpen,
      correctionMode,
      correctionDraftId,
      correctionSourceLabel,
      correctionSourceInvoice,
      correctionAdjustmentType,
      correctionReason,
      correctionCustomerName,
      correctionCustomerTaxId,
      correctionCustomerAddress,
      correctionCustomerEmail,
      correctionCustomerPhone,
      correctionPaymentMethod,
      correctionNote,
      correctionItems,
      correctionVatRate,
      correctionDiscountAmount,
      correctionTaxFeeAmount,
      correctionFeeOtherAmount,
      correctionIsDraft,
      correctionLoading,
      correctionOperationalPreview,
      correctionOperationalPreviewLoading,
      operationalMapping,
      operationalMappingLoading,
      fnbRecoveryLoading,
      correctionProducts,
      correctionProductSearch,
      correctionSelectedProductKey,
      correctionProductsLoading,
    } = this.state;
    // console.log("sepayUsage", sepayUsage);
    const visibleItems = this.getVisibleItems();
    const canRunBatchAction =
      selectedIds.length > 0 && selectedIds.length <= 20 && !batchActionLoading;
    const activeInvoice =
      visibleItems.find((invoice) => invoice.id === selectedInvoice?.id) ||
      visibleItems[0] ||
      null;
    const activeOperationalMapping =
      getInvoiceKey(operationalMapping?.einvoice_id) ===
      getInvoiceKey(activeInvoice?.id)
        ? operationalMapping
        : null;
    const automaticInventory =
      activeOperationalMapping?.result?.inventory || null;
    const inventoryRecovery =
      activeOperationalMapping?.inventory_recovery || null;
    const hasAutomaticInventory =
      Number(automaticInventory?.version || 0) >= 2 &&
      automaticInventory?.state === "applied";
    const automaticInventoryMessage = hasAutomaticInventory
      ? this.props.intl.formatMessage(
          { id: "einvoice.mapping.inventory-applied" },
          {
            restored: Number(automaticInventory.ingredients_restored || 0),
            deducted: Number(automaticInventory.ingredients_deducted || 0),
            variantsRestored: Number(automaticInventory.variants_restored || 0),
            variantsDeducted: Number(automaticInventory.variants_deducted || 0),
          },
        )
      : null;
    const allSelected =
      visibleItems.length > 0 &&
      visibleItems.every((invoice) => selectedIds.includes(invoice.id));
    const correctionSubtotal = correctionItems.reduce(
      (total, item) =>
        total + Number(item.quantity || 0) * Number(item.unit_price || 0),
      0,
    );
    const correctionVatAmount =
      correctionVatRate > 0
        ? Math.round((correctionSubtotal * correctionVatRate) / 100)
        : 0;
    const correctionTotal =
      correctionSubtotal -
      Number(correctionDiscountAmount || 0) +
      correctionVatAmount +
      Number(correctionTaxFeeAmount || 0) +
      Number(correctionFeeOtherAmount || 0);
    const columns = [
      {
        id: "selection",
        Header: () => (
          <Input
            type="checkbox"
            className="einvoice-workspace-selection-control"
            aria-label="Chọn tất cả hóa đơn"
            checked={allSelected}
            onClick={(event) => event.stopPropagation()}
            onChange={() => this.toggleAll(visibleItems)}
          />
        ),
        Cell: ({ original }) => (
          <Input
            type="checkbox"
            className="einvoice-workspace-selection-control"
            aria-label={`Chọn hóa đơn ${original.invoice_no || original.id}`}
            checked={selectedIds.includes(original.id)}
            onClick={(event) => event.stopPropagation()}
            onChange={() => this.toggleSelection(original.id)}
          />
        ),
        className: "einvoice-workspace-selection-cell",
        headerClassName: "einvoice-workspace-selection-cell",
        sortable: false,
        width: 44,
      },
      {
        Header: "Số hóa đơn",
        accessor: "invoice_no",
        minWidth: 95,
        Cell: ({ original, value }) => {
          const hierarchyDepth = Number(original.hierarchy_depth || 0);
          const isChild = isCorrectionInvoice(original);
          return (
            <div
              className={`einvoice-workspace-invoice-identity${
                isChild ? " is-child" : ""
              }`}
              style={
                hierarchyDepth
                  ? { paddingLeft: `${Math.min(hierarchyDepth, 3) * 18}px` }
                  : undefined
              }
            >
              {isChild && (
                <span
                  className="einvoice-workspace-hierarchy-connector"
                  title={`Thuộc hóa đơn nguồn #${original.parent_einvoice_id}`}
                >
                  ↳
                </span>
              )}
              <div>
                <button
                  type="button"
                  className="btn btn-link p-0 font-weight-bold einvoice-workspace-link"
                  onClick={() => this.selectInvoice(original)}
                >
                  {value || "Chưa cấp số"}
                </button>
                <small className="text-muted d-block">
                  {isChild
                    ? `${invoiceTypeLabel(original.invoice_type)} · ${
                        original.invoice_series || "—"
                      }`
                    : original.invoice_series || "—"}
                </small>
              </div>
            </div>
          );
        },
      },
      {
        Header: "Ngày lập",
        id: "date",
        accessor: (invoice) => invoice.invoice_date || invoice.createdAt,
        width: 88,
        Cell: ({ original }) => (
          <div>
            {date(original.invoice_date || original.createdAt)}
            <small className="text-muted d-block">
              {date(original.issued_at)}
            </small>
          </div>
        ),
      },
      {
        Header: "Khách hàng",
        id: "customer",
        accessor: (invoice) => invoice.customer_name || "",
        minWidth: 128,
        Cell: ({ original, value }) => (
          <div>
            <strong>{value || "Khách lẻ"}</strong>
            <small className="text-muted d-block">
              {original.customer_tax_id || "Chưa có MST"}
            </small>
          </div>
        ),
      },
      {
        Header: "Tổng tiền (VND)",
        accessor: "total_amount",
        minWidth: 90,
        className: "text-right",
        headerClassName: "text-right",
        Cell: ({ value }) => <strong>{money(value)}</strong>,
      },
      {
        Header: this.props.intl.formatMessage({
          id: "einvoice.filter.issuance-status",
        }),
        accessor: "issuance_status",
        minWidth: 96,
        Cell: ({ original }) => {
          const meta = statusMeta(original.issuance_status || original.status);
          return (
            <Badge color={meta.color} pill title={original.error_message}>
              {meta.label}
            </Badge>
          );
        },
      },
      {
        Header: "TT nhà cung cấp",
        id: "sync",
        accessor: (invoice) => syncMeta(invoice).label,
        minWidth: 96,
        Cell: ({ original }) => {
          const meta = syncMeta(original);
          return (
            <Badge color={meta.color} pill>
              {meta.label}
            </Badge>
          );
        },
      },
      {
        Header: "Loại hóa đơn",
        accessor: "invoice_type",
        minWidth: 92,
        Cell: ({ original, value }) => {
          const replaced = isReplacedInvoice(original, this.state.items);
          return (
            <span
              className={
                isCorrectionInvoice(original)
                  ? "einvoice-workspace-child-invoice-type"
                  : ""
              }
              title={
                replaced
                  ? "Hóa đơn này đã được thay thế bởi một hóa đơn khác"
                  : undefined
              }
            >
              {isCorrectionInvoice(original) ? "↳ " : ""}
              {replaced ? (
                <span className="text-danger font-weight-bold">
                  Hóa đơn đã bị thay thế
                </span>
              ) : (
                invoiceTypeLabel(value)
              )}
            </span>
          );
        },
      },
      {
        id: "actions",
        // Header: "Thao tác",
        sortable: false,
        width: 20,
        Cell: ({ original }) => {
          const replaced = isReplacedInvoice(original, this.state.items);
          const adjustmentLock = correctionEligibility(
            original,
            this.state.items,
            "adjustment",
          );
          const replacementLock = correctionEligibility(
            original,
            this.state.items,
            "replacement",
          );
          const showCorrectionActions =
            original.provider === "sepay" && !replaced;
          return (
            <div
              className="einvoice-workspace-row-actions"
              onClick={(event) => event.stopPropagation()}
            >
              <UncontrolledDropdown>
                <DropdownToggle
                  color="link"
                  size="xs"
                  caret={false}
                  className="einvoice-workspace-overflow-toggle"
                  title="Thao tác khác"
                >
                  <i className="simple-icon-options-vertical" />
                </DropdownToggle>
                <DropdownMenu
                  right
                  positionFixed
                  className="einvoice-workspace-action-menu"
                >
                  {isEditableSepayCorrection(original) && (
                    <DropdownItem
                      onClick={() =>
                        this.openCorrectionModal(
                          original,
                          original.invoice_type,
                        )
                      }
                    >
                      <i className="simple-icon-note mr-2" />{" "}
                      <IntlMessages id="einvoice.correction.edit-draft" />
                    </DropdownItem>
                  )}
                  {showCorrectionActions &&
                    !isEditableSepayCorrection(original) && (
                      <>
                        {original.invoice_type !== "replacement" && (
                          <DropdownItem
                            disabled={Boolean(adjustmentLock)}
                            title={
                              adjustmentLock || "Lập hóa đơn điều chỉnh SePay"
                            }
                            onClick={() =>
                              this.openCorrectionModal(original, "adjustment")
                            }
                          >
                            <i className="simple-icon-pencil mr-2" />{" "}
                            <IntlMessages id="einvoice.correction.adjust" />
                            {adjustmentLock ? ` — ${adjustmentLock}` : ""}
                          </DropdownItem>
                        )}
                        <DropdownItem
                          disabled={Boolean(replacementLock)}
                          title={
                            replacementLock || "Lập hóa đơn thay thế SePay"
                          }
                          onClick={() =>
                            this.openCorrectionModal(original, "replacement")
                          }
                        >
                          <i className="simple-icon-reload mr-2" />{" "}
                          <IntlMessages id="einvoice.correction.replace" />
                          {replacementLock ? ` — ${replacementLock}` : ""}
                        </DropdownItem>
                      </>
                    )}
                  {(original.status === "draft" ||
                    original.status === "error") &&
                    (!isEditableSepayCorrection(original) ||
                      hasSePayReference(original)) && (
                      <DropdownItem
                        onClick={() => this.handleIssueOfficial(original)}
                      >
                        <i className="simple-icon-check mr-2" />
                        {original.status === "error"
                          ? "Phát hành lại"
                          : "Phát hành"}
                      </DropdownItem>
                    )}
                  {(original.status === "draft" ||
                    original.status === "error") &&
                    !isEditableSepayCorrection(original) && (
                      <DropdownItem
                        onClick={() => this.openEditModal(original)}
                      >
                        <i className="simple-icon-note mr-2" /> Chỉnh sửa
                      </DropdownItem>
                    )}
                  {original.status !== "draft" && (
                    <DropdownItem
                      onClick={() => this.handleViewPdf(original.id)}
                    >
                      <i className="simple-icon-doc mr-2" /> Xem PDF
                    </DropdownItem>
                  )}
                  {original.status !== "draft" && (
                    <DropdownItem
                      onClick={() => this.handleDownloadPdf(original.id)}
                    >
                      <i className="simple-icon-cloud-download mr-2" /> Tải PDF
                    </DropdownItem>
                  )}
                  {original.status !== "draft" &&
                    original.status !== "cancelled" && (
                      <DropdownItem
                        onClick={() => this.openEmailModal(original)}
                      >
                        <i className="simple-icon-envelope mr-2" /> Gửi email
                      </DropdownItem>
                    )}
                  <DropdownItem
                    onClick={() => this.handleCheckStatus(original.id)}
                  >
                    <i className="simple-icon-refresh mr-2" /> Kiểm tra trạng
                    thái
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          );
        },
      },
    ];

    return (
      <>
        <div className="einvoice-workspace">
          {/* <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
            <Button color="primary" tag="a" href="/app/pages/order" className="mt-2 mt-sm-0">
              <i className="simple-icon-plus mr-2" /> Lập hóa đơn điện tử
            </Button>
          </div> */}

          <Row className="mb-3">
            <Col sm="6" xs="12" className="mb-3 einvoice-workspace-kpi-col">
              <EInvoiceMetricCard
                icon="simple-icon-doc"
                tone="primary"
                label="Tất cả hóa đơn"
                value={kpiSummary.totalCount}
                note={`Tổng tiền: ${money(kpiSummary.totalAmount)}`}
              />
            </Col>
            <Col sm="6" xs="12" className="mb-3 einvoice-workspace-kpi-col">
              <EInvoiceMetricCard
                icon="simple-icon-check"
                tone="success"
                label="Đã phát hành"
                value={kpiSummary.issuedCount}
                note={`Tổng tiền: ${money(kpiSummary.issuedAmount)}`}
              />
            </Col>
            <Col sm="6" xs="12" className="mb-3 einvoice-workspace-kpi-col">
              <EInvoiceMetricCard
                icon="simple-icon-clock"
                tone="warning"
                label="Chờ xử lý"
                value={kpiSummary.pendingCount}
                details={[
                  { label: "Nháp", value: kpiSummary.draftCount },
                  { label: "Chờ ký", value: kpiSummary.processingCount },
                  {
                    label: "Chưa xác định",
                    value: kpiSummary.pendingStatusCount,
                  },
                ]}
              />
            </Col>
            <Col sm="6" xs="12" className="mb-3 einvoice-workspace-kpi-col">
              <EInvoiceMetricCard
                icon="simple-icon-close"
                tone="danger"
                label="Cần kiểm tra"
                value={kpiSummary.attentionCount}
                details={[
                  { label: "Lỗi", value: kpiSummary.errorCount },
                  { label: "Hủy", value: kpiSummary.cancelledCount },
                ]}
              />
            </Col>
            <Col sm="6" xs="12" className="mb-3 einvoice-workspace-kpi-col">
              <EInvoiceMetricCard
                icon="simple-icon-chart"
                tone="info"
                label="Số hóa đơn còn lại"
                value={sepayUsage.remaining}
                // value={
                //   sepayUsage.isLoading
                //     ? "..."
                //     : sepayUsage.available
                //       ? sepayUsage.total > 0
                //         ? `${1000 - sepayUsage.used} / 1000`
                //         : // ? `${sepayUsage.used} / ${sepayUsage.total}`
                //           `${1000 - sepayUsage.remaining} / 1000`
                //       : // : `${sepayUsage.remaining}`
                //         "—"
                // }
                // note={
                //   sepayUsage.isLoading
                //     ? "Đang tải từ SePay"
                //     : sepayUsage.available
                //       ? `Còn lại: ${sepayUsage.remaining} hóa đơn`
                //       : sepayUsage.hasError
                //         ? "Không thể tải dữ liệu SePay"
                //         : "Chưa kết nối SePay"
                // }
              />
            </Col>
          </Row>

          <Row className="einvoice-workspace-main-row">
            <Col xl="9" className="min-width-zero mb-5">
              <Card className="einvoice-workspace-filter-card mb-3">
                <CardBody>
                  <Row className="align-items-end justify-content-end">
                    <Col md="6" lg="4" className="mb-2">
                      <Label className="small text-muted mb-1">
                        Khoảng ngày lập
                      </Label>
                      <DatePicker
                        selected={dateStart ? moment(dateStart) : null}
                        startDate={dateStart ? moment(dateStart) : null}
                        endDate={dateEnd ? moment(dateEnd) : null}
                        onChange={this.handleInvoiceDateChange}
                        onFocus={() => this.setState({ datePickerOpen: true })}
                        onInputClick={() =>
                          this.setState({ datePickerOpen: true })
                        }
                        onClickOutside={() =>
                          this.setState({ datePickerOpen: false })
                        }
                        open={datePickerOpen}
                        shouldCloseOnSelect={false}
                        allowSameDay
                        isClearable
                        readOnly
                        dateFormat="DD/MM/YYYY"
                        placeholderText="Chọn khoảng ngày lập"
                        value={
                          dateStart
                            ? `${moment(dateStart).format("DD/MM/YYYY")}${dateEnd ? ` — ${moment(dateEnd).format("DD/MM/YYYY")}` : ""}`
                            : ""
                        }
                        className="form-control"
                        popperPlacement="bottom-start"
                      />
                    </Col>
                    <Col md="4" lg="2" className="mb-2">
                      <Label className="small text-muted mb-1">
                        Loại hóa đơn
                      </Label>
                      <Input
                        type="select"
                        value={invoiceType}
                        onChange={(event) =>
                          this.setClientFilter(
                            "invoiceType",
                            event.target.value,
                          )
                        }
                      >
                        <option value="">Tất cả</option>
                        <option value="original">Hóa đơn gốc</option>
                        <option value="adjustment">Điều chỉnh</option>
                        <option value="replacement">Hóa đơn thay thế</option>
                      </Input>
                    </Col>
                    <Col md="4" lg="2" className="mb-2">
                      <Label className="small text-muted mb-1">
                        {this.props.intl.formatMessage({
                          id: "einvoice.filter.issuance-status",
                        })}
                      </Label>
                      <Input
                        type="select"
                        value={issuanceStatus}
                        onChange={(event) =>
                          this.setServerFilter(
                            "issuanceStatus",
                            event.target.value,
                          )
                        }
                      >
                        {ISSUANCE_STATUS_OPTIONS.map((option) => (
                          <option
                            key={option.value || "all"}
                            value={option.value}
                          >
                            {this.props.intl.formatMessage({
                              id: option.labelId,
                            })}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md="4" lg="2" className="mb-2">
                      <Label className="small text-muted mb-1">
                        {this.props.intl.formatMessage({
                          id: "einvoice.filter.invoice-status",
                        })}
                      </Label>
                      <Input
                        type="select"
                        value={invoiceStatus}
                        onChange={(event) =>
                          this.setServerFilter(
                            "invoiceStatus",
                            event.target.value,
                          )
                        }
                      >
                        {INVOICE_STATUS_OPTIONS.map((option) => (
                          <option
                            key={option.value || "all"}
                            value={option.value}
                          >
                            {this.props.intl.formatMessage({
                              id: option.labelId,
                            })}
                          </option>
                        ))}
                      </Input>
                    </Col>
                  </Row>
                  <Row className="align-items-center mt-1">
                    {/* <Col lg="6" className="mb-2 mb-lg-0">
                      <Input
                        value={keyword}
                        placeholder="Tìm theo số hóa đơn, mã khách hàng, tên khách hàng, mã số thuế..."
                        onChange={(event) =>
                          this.setClientFilter("keyword", event.target.value)
                        }
                      />
                    </Col> */}
                    <Col lg="12" className="text-lg-right">
                      <Button
                        color="outline-primary"
                        size="sm"
                        className="mr-2"
                        onClick={this.clearFilters}
                      >
                        Bỏ lọc
                      </Button>
                      <Button
                        color="light"
                        size="sm"
                        className="mr-2"
                        onClick={this.fetchData}
                      >
                        <i className="simple-icon-refresh mr-1" />
                        Tải lại
                      </Button>
                      <Button
                        color="light"
                        size="sm"
                        className="mr-2"
                        disabled={!visibleItems.length}
                        onClick={() => this.exportInvoices(visibleItems)}
                      >
                        <i className="simple-icon-cloud-download mr-1" />
                        Xuất Excel
                      </Button>
                      <UncontrolledDropdown className="d-inline-block">
                        <DropdownToggle color="light" size="sm" caret>
                          Thao tác
                        </DropdownToggle>
                        <DropdownMenu right>
                          <DropdownItem
                            disabled={!canRunBatchAction}
                            onClick={this.handleBatchDownloadPdf}
                          >
                            <i className="simple-icon-cloud-download mr-2" />
                            {batchActionLoading === "download"
                              ? "Đang tạo tệp ZIP..."
                              : "Tải PDF đã chọn"}
                          </DropdownItem>
                          <DropdownItem
                            disabled={!canRunBatchAction}
                            onClick={this.handleBatchSendEmail}
                          >
                            <i className="simple-icon-envelope mr-2" />
                            {batchActionLoading === "email"
                              ? "Đang gửi email..."
                              : "Gửi email khách hàng"}
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card className="einvoice-workspace-table-card">
                {isLoading ? <div className="loading" /> : null}
                <div className="einvoice-workspace-table-scroll">
                  <ReactTable
                    data={visibleItems}
                    columns={columns}
                    minRows={0}
                    pageSize={Math.max(visibleItems.length, 1)}
                    showPagination={false}
                    sortable={false}
                    className="-highlight einvoice-workspace-table"
                    getTrProps={(tableState, rowInfo) =>
                      rowInfo
                        ? (() => {
                            const replaced = isReplacedInvoice(
                              rowInfo.original,
                              this.state.items,
                            );
                            return {
                              onClick: () =>
                                this.selectInvoice(rowInfo.original),
                              className: [
                                activeInvoice &&
                                rowInfo.original.id === activeInvoice.id
                                  ? "is-active"
                                  : "",
                                isCorrectionInvoice(rowInfo.original)
                                  ? "is-child-invoice"
                                  : "",
                                replaced ? "is-replaced-invoice" : "",
                              ]
                                .filter(Boolean)
                                .join(" "),
                            };
                          })()
                        : {}
                    }
                    getTdProps={(tableState, rowInfo, column) =>
                      rowInfo &&
                      column.id !== "actions" &&
                      isReplacedInvoice(rowInfo.original, this.state.items)
                        ? {
                            style: {
                              color: "#6c757d",
                              opacity: 0.62,
                            },
                          }
                        : {}
                    }
                    NoDataComponent={() => (
                      <div className="text-center text-muted py-5">
                        Không có hóa đơn phù hợp.
                      </div>
                    )}
                  />
                </div>
                <CardBody className="border-top py-3">
                  <Row className="align-items-center">
                    <Col md="2" className="small text-muted mb-2 mb-md-0">
                      Hiển thị {visibleItems.length} / {totalItem} hóa đơn
                    </Col>
                    <Col md="8" className="mb-2 mb-md-0">
                      <Pagination
                        currentPage={currentPage}
                        totalPage={totalPage}
                        onChangePage={this.onChangePage}
                      />
                    </Col>
                    <Col md="2" className="text-md-right">
                      <Input
                        type="select"
                        value={selectedPageSize}
                        className="d-inline-block w-auto"
                        onChange={this.changePageSize}
                      >
                        {[10, 20, 50, 100].map((size) => (
                          <option key={size} value={size}>
                            {size} / trang
                          </option>
                        ))}
                      </Input>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col xl="3" className="mt-xl-0 mb-5">
              <Card className="einvoice-workspace-detail-card">
                <CardHeader className="d-flex justify-content-between align-items-center mt-3">
                  <strong>Chi tiết hóa đơn điện tử</strong>
                  <Button
                    close
                    onClick={() => this.setState({ selectedInvoice: null })}
                  />
                </CardHeader>
                {!activeInvoice ? (
                  <CardBody className="text-muted py-5 text-center">
                    Chọn một hóa đơn để xem chi tiết.
                  </CardBody>
                ) : (
                  <>
                    <CardBody className="pb-0">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <strong className="h5 text-primary">
                            {activeInvoice.invoice_no || "Chưa cấp số"}
                          </strong>
                          <small className="d-block text-muted">
                            Ký hiệu: {activeInvoice.invoice_series || "—"}
                          </small>
                        </div>
                        <Badge
                          color={statusMeta(activeInvoice.status).color}
                          pill
                        >
                          {statusMeta(activeInvoice.status).label}
                        </Badge>
                      </div>
                      <div className="small text-muted mt-3">
                        Ngày lập:{" "}
                        {date(
                          activeInvoice.invoice_date || activeInvoice.createdAt,
                        )}{" "}
                        · Ngày phát hành: {date(activeInvoice.issued_at)}
                      </div>
                    </CardBody>
                    <Nav tabs className="einvoice-workspace-detail-tabs mt-3">
                      <NavItem>
                        <NavLink
                          href="#info"
                          className={detailTab === "info" ? "active" : ""}
                          onClick={(event) => {
                            event.preventDefault();
                            this.setState({ detailTab: "info" });
                          }}
                        >
                          Thông tin
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#history"
                          className={detailTab === "history" ? "active" : ""}
                          onClick={(event) => {
                            event.preventDefault();
                            this.setState({ detailTab: "history" });
                          }}
                        >
                          Lịch sử
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#other"
                          className={detailTab === "other" ? "active" : ""}
                          onClick={(event) => {
                            event.preventDefault();
                            this.setState({ detailTab: "other" });
                          }}
                        >
                          Khác
                        </NavLink>
                      </NavItem>
                    </Nav>
                    <CardBody>
                      {detailTab === "info" && (
                        <>
                          <h6>Khách hàng</h6>
                          <DetailRow
                            label="Tên khách hàng"
                            value={activeInvoice.customer_name || "Khách lẻ"}
                          />
                          <DetailRow
                            label="Mã số thuế"
                            value={activeInvoice.customer_tax_id}
                          />
                          <DetailRow
                            label="Địa chỉ"
                            value={activeInvoice.customer_address}
                          />
                          <DetailRow
                            label="Phương thức TT"
                            value={activeInvoice.payment_method}
                          />
                          <hr />
                          <h6>Thanh toán</h6>
                          <DetailRow
                            label="Tổng tiền trước thuế"
                            value={money(activeInvoice.subtotal)}
                          />
                          <DetailRow
                            label={`Thuế GTGT (${activeInvoice.vat_rate || 0}%)`}
                            value={money(activeInvoice.vat_amount)}
                          />
                          <DetailRow
                            label="Tổng tiền thanh toán"
                            value={money(activeInvoice.total_amount)}
                            danger
                          />
                          <hr />
                          <h6>Trạng thái xử lý</h6>
                          <DetailRow
                            label="Phát hành"
                            value={statusMeta(activeInvoice.status).label}
                          />
                          <DetailRow
                            label="Đồng bộ nhà cung cấp"
                            value={syncMeta(activeInvoice).label}
                          />
                          {isCorrectionInvoice(activeInvoice) && (
                            <>
                              <DetailRow
                                label="Đồng bộ VMASS"
                                value={
                                  operationalMappingLoading
                                    ? "Đang tải audit..."
                                    : activeOperationalMapping?.state ===
                                        "applied"
                                      ? "Đã áp dụng"
                                      : activeOperationalMapping?.state ===
                                          "conflict"
                                        ? "Xung đột, cần lập chứng từ mới"
                                        : activeOperationalMapping?.state ===
                                            "failed"
                                          ? "Lỗi kỹ thuật, sẽ tự thử lại"
                                          : "Đang chờ áp dụng"
                                }
                                danger={
                                  activeOperationalMapping?.state === "conflict"
                                }
                              />
                              {hasAutomaticInventory ? (
                                <DetailRow
                                  label={this.props.intl.formatMessage({
                                    id: "einvoice.mapping.inventory",
                                  })}
                                  value={automaticInventoryMessage}
                                />
                              ) : null}
                              {activeOperationalMapping?.error_message ? (
                                <small className="text-danger d-block mt-1">
                                  {activeOperationalMapping.error_message}
                                </small>
                              ) : null}
                              {inventoryRecovery?.can_recover ? (
                                <Button
                                  color="outline-warning"
                                  size="xs"
                                  className="mt-2"
                                  disabled={fnbRecoveryLoading}
                                  onClick={() =>
                                    this.recoverFnbRecipeIngredients(
                                      activeInvoice,
                                    )
                                  }
                                >
                                  {fnbRecoveryLoading
                                    ? "Đang khôi phục nguyên liệu..."
                                    : "Khôi phục nguyên liệu F&B"}
                                </Button>
                              ) : inventoryRecovery?.status === "recovered" ? (
                                <small className="text-success d-block mt-1">
                                  Đã khôi phục nguyên liệu F&B theo định lượng.
                                </small>
                              ) : null}
                            </>
                          )}
                        </>
                      )}
                      {detailTab === "history" && (
                        <div className="small">
                          <DetailRow
                            label="Khởi tạo"
                            value={dateTime(activeInvoice.createdAt)}
                          />
                          <DetailRow
                            label="Phát hành"
                            value={dateTime(activeInvoice.issued_at)}
                          />
                          <DetailRow
                            label="Gửi email"
                            value={dateTime(activeInvoice.sent_email_at)}
                          />
                        </div>
                      )}
                      {detailTab === "other" && (
                        <div className="small">
                          <DetailRow
                            label="Nhà cung cấp"
                            value={
                              activeInvoice.provider
                                ? activeInvoice.provider.toUpperCase()
                                : "—"
                            }
                          />
                          <DetailRow
                            label="Mã nhà cung cấp"
                            value={activeInvoice.provider_invoice_id}
                          />
                          <DetailRow
                            label="Ghi chú"
                            value={activeInvoice.note}
                          />
                        </div>
                      )}
                      <hr />
                      <h6>Tệp hóa đơn</h6>
                      <div className="d-flex">
                        <Button
                          color="light"
                          size="sm"
                          className="mr-2"
                          disabled={activeInvoice.status === "draft"}
                          onClick={() => this.handleViewPdf(activeInvoice.id)}
                        >
                          Xem PDF
                        </Button>
                        <Button
                          color="light"
                          size="sm"
                          disabled={activeInvoice.status === "draft"}
                          onClick={() =>
                            this.handleDownloadPdf(activeInvoice.id)
                          }
                        >
                          Tải PDF
                        </Button>
                      </div>
                    </CardBody>
                    <CardBody className="border-top pt-3">
                      <div className="d-flex">
                        <Button
                          color="primary"
                          size="sm"
                          className="mr-2"
                          disabled={
                            activeInvoice.status === "draft" ||
                            activeInvoice.status === "cancelled"
                          }
                          onClick={() => this.openEmailModal(activeInvoice)}
                        >
                          Gửi khách hàng
                        </Button>
                        {(activeInvoice.status === "draft" ||
                          activeInvoice.status === "error") && (
                          <Button
                            color="outline-primary"
                            size="sm"
                            disabled={issueLoading}
                            onClick={() =>
                              this.handleIssueOfficial(activeInvoice)
                            }
                          >
                            {issueLoading ? "Đang xử lý" : "Phát hành"}
                          </Button>
                        )}
                      </div>
                    </CardBody>
                  </>
                )}
              </Card>
            </Col>
          </Row>
          {isCompactDesktop && activeInvoice && (
            <Modal
              isOpen={detailDrawerOpen && Boolean(activeInvoice)}
              toggle={() => this.setState({ detailDrawerOpen: false })}
              className="einvoice-workspace-detail-drawer"
              wrapClassName="modal-right"
            >
              <ModalBody className="p-0 mt-3 mb-3 scrool modal-right-add">
                <PerfectScrollbar
                  options={{ suppressScrollX: true, wheelPropagation: false }}
                >
                  <Card className="einvoice-workspace-detail-card">
                    <CardHeader className="d-flex justify-content-between align-items-center">
                      <strong>Chi tiết hóa đơn điện tử</strong>
                      <Button
                        close
                        onClick={() =>
                          this.setState({ detailDrawerOpen: false })
                        }
                      />
                    </CardHeader>
                    <CardBody className="pb-0">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <strong className="h5 text-primary">
                            {activeInvoice.invoice_no || "Chưa cấp số"}
                          </strong>
                          <small className="d-block text-muted">
                            Ký hiệu: {activeInvoice.invoice_series || "—"}
                          </small>
                        </div>
                        <Badge
                          color={statusMeta(activeInvoice.status).color}
                          pill
                        >
                          {statusMeta(activeInvoice.status).label}
                        </Badge>
                      </div>
                      <div className="small text-muted mt-3">
                        Ngày lập:{" "}
                        {date(
                          activeInvoice.invoice_date || activeInvoice.createdAt,
                        )}{" "}
                        · Ngày phát hành: {date(activeInvoice.issued_at)}
                      </div>
                    </CardBody>
                    <Nav tabs className="einvoice-workspace-detail-tabs mt-3">
                      <NavItem>
                        <NavLink
                          href="#info"
                          className={detailTab === "info" ? "active" : ""}
                          onClick={(event) => {
                            event.preventDefault();
                            this.setState({ detailTab: "info" });
                          }}
                        >
                          Thông tin
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#history"
                          className={detailTab === "history" ? "active" : ""}
                          onClick={(event) => {
                            event.preventDefault();
                            this.setState({ detailTab: "history" });
                          }}
                        >
                          Lịch sử
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          href="#other"
                          className={detailTab === "other" ? "active" : ""}
                          onClick={(event) => {
                            event.preventDefault();
                            this.setState({ detailTab: "other" });
                          }}
                        >
                          Khác
                        </NavLink>
                      </NavItem>
                    </Nav>
                    <CardBody>
                      {detailTab === "info" && (
                        <>
                          <h6>Khách hàng</h6>
                          <DetailRow
                            label="Tên khách hàng"
                            value={activeInvoice.customer_name || "Khách lẻ"}
                          />
                          <DetailRow
                            label="Mã số thuế"
                            value={activeInvoice.customer_tax_id}
                          />
                          <DetailRow
                            label="Địa chỉ"
                            value={activeInvoice.customer_address}
                          />
                          <DetailRow
                            label="Phương thức TT"
                            value={activeInvoice.payment_method}
                          />
                          <hr />
                          <h6>Thanh toán</h6>
                          <DetailRow
                            label="Tổng tiền trước thuế"
                            value={money(activeInvoice.subtotal)}
                          />
                          <DetailRow
                            label={`Thuế GTGT (${activeInvoice.vat_rate || 0}%)`}
                            value={money(activeInvoice.vat_amount)}
                          />
                          <DetailRow
                            label="Tổng tiền thanh toán"
                            value={money(activeInvoice.total_amount)}
                            danger
                          />
                          <hr />
                          <h6>Trạng thái xử lý</h6>
                          <DetailRow
                            label="Phát hành"
                            value={statusMeta(activeInvoice.status).label}
                          />
                          <DetailRow
                            label="Đồng bộ nhà cung cấp"
                            value={syncMeta(activeInvoice).label}
                          />
                        </>
                      )}
                      {detailTab === "history" && (
                        <div className="small">
                          <DetailRow
                            label="Khởi tạo"
                            value={dateTime(activeInvoice.createdAt)}
                          />
                          <DetailRow
                            label="Phát hành"
                            value={dateTime(activeInvoice.issued_at)}
                          />
                          <DetailRow
                            label="Gửi email"
                            value={dateTime(activeInvoice.sent_email_at)}
                          />
                        </div>
                      )}
                      {detailTab === "other" && (
                        <div className="small">
                          <DetailRow
                            label="Nhà cung cấp"
                            value={
                              activeInvoice.provider
                                ? activeInvoice.provider.toUpperCase()
                                : "—"
                            }
                          />
                          <DetailRow
                            label="Mã nhà cung cấp"
                            value={activeInvoice.provider_invoice_id}
                          />
                          <DetailRow
                            label="Ghi chú"
                            value={activeInvoice.note}
                          />
                        </div>
                      )}
                      <hr />
                      <h6>Tệp hóa đơn</h6>
                      <div className="d-flex">
                        <Button
                          color="light"
                          size="sm"
                          className="mr-2"
                          disabled={activeInvoice.status === "draft"}
                          onClick={() => this.handleViewPdf(activeInvoice.id)}
                        >
                          Xem PDF
                        </Button>
                        <Button
                          color="light"
                          size="sm"
                          disabled={activeInvoice.status === "draft"}
                          onClick={() =>
                            this.handleDownloadPdf(activeInvoice.id)
                          }
                        >
                          Tải PDF
                        </Button>
                      </div>
                    </CardBody>
                    <CardBody className="border-top pt-3">
                      <div className="d-flex">
                        <Button
                          color="primary"
                          size="sm"
                          className="mr-2"
                          disabled={
                            activeInvoice.status === "draft" ||
                            activeInvoice.status === "cancelled"
                          }
                          onClick={() => this.openEmailModal(activeInvoice)}
                        >
                          Gửi khách hàng
                        </Button>
                        {(activeInvoice.status === "draft" ||
                          activeInvoice.status === "error") && (
                          <Button
                            color="outline-primary"
                            size="sm"
                            disabled={issueLoading}
                            onClick={() =>
                              this.handleIssueOfficial(activeInvoice)
                            }
                          >
                            {issueLoading ? "Đang xử lý" : "Phát hành"}
                          </Button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </PerfectScrollbar>
              </ModalBody>
            </Modal>
          )}
          <Modal
            isOpen={correctionModalOpen}
            toggle={this.closeCorrectionModal}
            size="lg"
          >
            <ModalHeader toggle={this.closeCorrectionModal}>
              {correctionMode === "replacement"
                ? this.props.intl.formatMessage({
                    id: "einvoice.correction.modal-replace-title",
                  })
                : this.props.intl.formatMessage({
                    id: "einvoice.correction.modal-adjust-title",
                  })}
            </ModalHeader>
            <ModalBody>
              <div className="alert alert-info border border-info mb-3">
                <div className="font-weight-bold text-dark">
                  Hóa đơn nguồn:{" "}
                  <span className="text-primary">
                    {correctionSourceLabel || "—"}
                  </span>
                </div>
                <div className="small text-dark mt-1">
                  Sau khi SePay xác nhận thành công, VMASS sẽ đồng bộ bill, tồn
                  kho, người mua và chênh lệch thanh toán từ chứng từ này.
                </div>
              </div>
              {correctionOperationalPreviewLoading ? (
                <div className="small text-muted mb-3">
                  Đang tải preview đồng bộ VMASS...
                </div>
              ) : correctionOperationalPreview ? (
                <div
                  className={`alert mb-3 ${
                    correctionOperationalPreview.source_snapshot_matches
                      ? "alert-light border"
                      : "alert-warning"
                  }`}
                >
                  <strong>
                    <IntlMessages id="einvoice.mapping.preview" />
                  </strong>
                  <div className="small mt-1">
                    {correctionOperationalPreview.source_snapshot_matches
                      ? "Bill chưa thay đổi kể từ lúc gửi yêu cầu."
                      : "Bill đã thay đổi; VMASS sẽ khóa áp dụng để tránh ghi đè dữ liệu."}
                  </div>
                  <div className="small mt-1">
                    Chênh lệch:{" "}
                    {money(
                      correctionOperationalPreview.settlement?.amount || 0,
                    )}{" "}
                    ·{" "}
                    {correctionOperationalPreview.settlement?.direction ===
                    "receivable"
                      ? "phải thu"
                      : correctionOperationalPreview.settlement?.direction ===
                          "refund_credit"
                        ? "hoàn/bù trừ"
                        : "không phát sinh"}
                    {correctionOperationalPreview.negative_stock_warning?.length
                      ? ` · ${correctionOperationalPreview.negative_stock_warning.length} dòng có thể làm tồn kho âm`
                      : ""}
                  </div>
                </div>
              ) : null}
              {correctionMode === "adjustment" && (
                <FormGroup>
                  <Label>Loại điều chỉnh</Label>
                  <Input
                    type="select"
                    value={correctionAdjustmentType}
                    onChange={(event) => {
                      const adjustmentType = event.target.value;
                      this.setState({
                        correctionAdjustmentType: adjustmentType,
                        correctionReason: getDefaultAdjustmentReason(
                          correctionSourceInvoice,
                          adjustmentType,
                        ),
                      });
                    }}
                  >
                    <option value="increase">Điều chỉnh tăng</option>
                    <option value="decrease">Điều chỉnh giảm</option>
                    <option value="combined">Điều chỉnh tăng/giảm</option>
                    <option value="info">Điều chỉnh thông tin</option>
                  </Input>
                </FormGroup>
              )}
              <FormGroup>
                <Label>
                  Lý do{" "}
                  {correctionMode === "replacement" ? "thay thế" : "điều chỉnh"}
                </Label>
                <Input
                  value={correctionReason}
                  onChange={(event) =>
                    this.setState({ correctionReason: event.target.value })
                  }
                  placeholder="Nhập lý do bắt buộc"
                />
              </FormGroup>
              <Row form>
                <Col md="6">
                  <FormGroup>
                    <Label>Tên người mua</Label>
                    <Input
                      value={correctionCustomerName}
                      onChange={(event) =>
                        this.setState({
                          correctionCustomerName: event.target.value,
                        })
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Mã số thuế</Label>
                    <Input
                      value={correctionCustomerTaxId}
                      onChange={(event) =>
                        this.setState({
                          correctionCustomerTaxId: event.target.value,
                        })
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Địa chỉ</Label>
                <Input
                  value={correctionCustomerAddress}
                  onChange={(event) =>
                    this.setState({
                      correctionCustomerAddress: event.target.value,
                    })
                  }
                />
              </FormGroup>
              <Row form>
                <Col md="6">
                  <FormGroup>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={correctionCustomerEmail}
                      onChange={(event) =>
                        this.setState({
                          correctionCustomerEmail: event.target.value,
                        })
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Điện thoại</Label>
                    <Input
                      value={correctionCustomerPhone}
                      onChange={(event) =>
                        this.setState({
                          correctionCustomerPhone: event.target.value,
                        })
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>
              {correctionMode === "adjustment" &&
              correctionAdjustmentType === "info" ? (
                <div className="alert alert-info small">
                  Điều chỉnh thông tin luôn có tổng tiền bằng 0 và SePay chỉ
                  nhận dòng ghi chú cùng thông tin người mua.
                </div>
              ) : (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <Label className="mb-0">Hàng hóa/dịch vụ</Label>
                  </div>
                  <Row form className="align-items-end mb-2">
                    <Col md="4">
                      <Label className="small text-muted mb-1">
                        Tìm sản phẩm
                      </Label>
                      <Input
                        bsSize="sm"
                        value={correctionProductSearch}
                        onChange={(event) =>
                          this.setState(
                            { correctionProductSearch: event.target.value },
                            () =>
                              this.fetchCorrectionProducts(event.target.value),
                          )
                        }
                        placeholder="Tên hoặc mã sản phẩm"
                      />
                    </Col>
                    <Col md="6">
                      <Label className="small text-muted mb-1">
                        Sản phẩm/biến thể mới
                      </Label>
                      <Input
                        bsSize="sm"
                        type="select"
                        value={correctionSelectedProductKey}
                        disabled={
                          correctionProductsLoading ||
                          !this.getCorrectionProductRows(correctionProducts)
                            .length
                        }
                        onChange={(event) =>
                          this.setState({
                            correctionSelectedProductKey: event.target.value,
                          })
                        }
                      >
                        {!this.getCorrectionProductRows(correctionProducts)
                          .length ? (
                          <option value="">Không có sản phẩm phù hợp</option>
                        ) : (
                          this.getCorrectionProductRows(correctionProducts).map(
                            (row) => (
                              <option key={row.key} value={row.key}>
                                {row.label}
                              </option>
                            ),
                          )
                        )}
                      </Input>
                    </Col>
                    <Col md="2">
                      <Button
                        color="outline-primary"
                        size="sm"
                        block
                        type="button"
                        disabled={correctionProductsLoading}
                        onClick={this.addCorrectionItem}
                      >
                        Thêm món
                      </Button>
                    </Col>
                  </Row>
                  <small className="text-muted d-block mb-2">
                    Món mới bắt buộc chọn từ catalog; VMASS giữ mã sản phẩm,
                    biến thể và modifier để đồng bộ tồn kho.
                  </small>
                  <div className="table-responsive border rounded mb-3">
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Hàng hóa/dịch vụ</th>
                          <th style={{ width: 92 }}>ĐVT</th>
                          <th style={{ width: 90 }}>SL</th>
                          <th style={{ width: 130 }}>Đơn giá</th>
                          <th style={{ width: 85 }}>Thành tiền</th>
                          <th style={{ width: 36 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {correctionItems.map((item, index) => (
                          <tr
                            key={`${item.item_key || item.item_code || "item"}-${index}`}
                          >
                            <td>
                              <Input
                                bsSize="sm"
                                value={item.item_name || ""}
                                onChange={(event) =>
                                  this.updateCorrectionItem(
                                    index,
                                    "item_name",
                                    event.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Input
                                bsSize="sm"
                                value={item.unit || "cái"}
                                onChange={(event) =>
                                  this.updateCorrectionItem(
                                    index,
                                    "unit",
                                    event.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Input
                                bsSize="sm"
                                type="number"
                                min="0"
                                step="1"
                                value={item.quantity}
                                onChange={(event) =>
                                  this.updateCorrectionItem(
                                    index,
                                    "quantity",
                                    event.target.value,
                                  )
                                }
                              />
                            </td>
                            <td>
                              <Input
                                bsSize="sm"
                                type="number"
                                min="0"
                                value={item.unit_price}
                                onChange={(event) =>
                                  this.updateCorrectionItem(
                                    index,
                                    "unit_price",
                                    event.target.value,
                                  )
                                }
                              />
                            </td>
                            <td className="text-right align-middle">
                              {money(
                                Number(item.quantity || 0) *
                                  Number(item.unit_price || 0),
                              )}
                            </td>
                            <td className="align-middle">
                              <Button
                                color="link"
                                size="xs"
                                type="button"
                                title="Xóa dòng"
                                onClick={() => this.removeCorrectionItem(index)}
                              >
                                ×
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {correctionMode === "adjustment" && (
                    <small className="text-muted d-block mb-3">
                      SePay tự thêm tiền tố “Điều chỉnh tăng/giảm mặt hàng” theo
                      dấu chênh lệch thực tế và server sẽ kiểm tra lại loại điều
                      chỉnh.
                    </small>
                  )}
                  <Row form className="mb-2">
                    <Col md="3">
                      <Label className="small mb-1">VAT (%)</Label>
                      <Input
                        bsSize="sm"
                        type="number"
                        min="0"
                        value={correctionVatRate}
                        onChange={(event) =>
                          this.setState({
                            correctionVatRate: Number(event.target.value || 0),
                          })
                        }
                      />
                    </Col>
                    <Col md="3">
                      <Label className="small mb-1">Chiết khấu</Label>
                      <Input
                        bsSize="sm"
                        type="number"
                        min="0"
                        disabled={correctionMode === "replacement"}
                        value={correctionDiscountAmount}
                        onChange={(event) =>
                          this.setState({
                            correctionDiscountAmount: Number(
                              event.target.value || 0,
                            ),
                          })
                        }
                      />
                    </Col>
                    <Col md="3">
                      <Label className="small mb-1">Thuế/phí</Label>
                      <Input
                        bsSize="sm"
                        type="number"
                        min="0"
                        disabled={correctionMode === "replacement"}
                        value={correctionTaxFeeAmount}
                        onChange={(event) =>
                          this.setState({
                            correctionTaxFeeAmount: Number(
                              event.target.value || 0,
                            ),
                          })
                        }
                      />
                    </Col>
                    <Col md="3">
                      <Label className="small mb-1">Phụ phí</Label>
                      <Input
                        bsSize="sm"
                        type="number"
                        min="0"
                        disabled={correctionMode === "replacement"}
                        value={correctionFeeOtherAmount}
                        onChange={(event) =>
                          this.setState({
                            correctionFeeOtherAmount: Number(
                              event.target.value || 0,
                            ),
                          })
                        }
                      />
                    </Col>
                  </Row>
                  <Row className="small text-right mb-3">
                    <Col>
                      Tiền hàng: <strong>{money(correctionSubtotal)}</strong>
                    </Col>
                    <Col>
                      VAT ({correctionVatRate}%):{" "}
                      <strong>{money(correctionVatAmount)}</strong>
                    </Col>
                    <Col>
                      Tổng: <strong>{money(correctionTotal)}</strong>
                    </Col>
                  </Row>
                </>
              )}
              <Row form>
                <Col md="5">
                  <FormGroup>
                    <Label>Phương thức thanh toán</Label>
                    <Input
                      value={correctionPaymentMethod}
                      onChange={(event) =>
                        this.setState({
                          correctionPaymentMethod: event.target.value,
                        })
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md="7">
                  <FormGroup>
                    <Label>Ghi chú</Label>
                    <Input
                      value={correctionNote}
                      onChange={(event) =>
                        this.setState({ correctionNote: event.target.value })
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={correctionIsDraft}
                    onChange={(event) =>
                      this.setState({ correctionIsDraft: event.target.checked })
                    }
                  />{" "}
                  <IntlMessages id="einvoice.correction.save-draft" /> (
                  <IntlMessages id="einvoice.correction.issue-now" />)
                </Label>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <small className="text-muted mr-auto">
                Xác nhận:{" "}
                {correctionMode === "replacement" ? "thay thế" : "điều chỉnh"}{" "}
                hóa đơn nguồn {correctionSourceLabel || "—"} ·{" "}
                {correctionIsDraft ? "nháp" : "phát hành ngay"}
                {correctionDraftId ? " · cập nhật nháp đang có" : ""}
              </small>
              <Button color="secondary" onClick={this.closeCorrectionModal}>
                Hủy
              </Button>
              <Button
                color="primary"
                disabled={correctionLoading}
                onClick={this.handleSubmitCorrection}
              >
                {correctionLoading ? (
                  "Đang gửi..."
                ) : (
                  <IntlMessages id="einvoice.correction.submit" />
                )}
              </Button>
            </ModalFooter>
          </Modal>
        </div>
        <div className="d-none">{this.renderLegacy()}</div>
      </>
    );
  }
}

export default injectIntl(EInvoiceList);
